import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, LayersControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { PosteCarte, StatutCarte } from '@/services/cartePostesService';

/**
 * Carte des postes de stationnement (SFD "Carte du statut hivernal des
 * postes" section 6). Conversion TypeScript du composant de depart
 * `docs/maps/ParkingMap.jsx` : CircleMarker (pas l'icone par defaut de
 * Leaflet, pour eviter le probleme classique d'icones cassees sous
 * webpack), couleurs alignees sur les 6 vraies valeurs de `statutCarte`
 * (section 2 de la SFD), pas celles du composant de depart (libre/planifie/
 * en_cours/termine/valide, qui ne correspondent pas au modele Falcon).
 */

// Centres de reference (ARP AIP) utilises quand aucun poste geolocalise n'est charge.
const AEROPORTS = {
  ORY: { nom: 'Paris-Orly', centre: [48.7233, 2.3794] as [number, number], zoom: 15 },
  CDG: { nom: 'Paris-Charles de Gaulle', centre: [49.0097, 2.5479] as [number, number], zoom: 14 },
};

export const COULEURS_STATUT_CARTE: Record<StatutCarte, string> = {
  indisponible: '#64748B',
  en_cours: '#EF4444',
  en_attente_deneignement: '#F59E0B',
  demande: '#3B82F6',
  effectue: '#22C55E',
  aucune: '#FFFFFF',
};

/**
 * Cadre la vue au changement d'aeroport ou de `zoomKey` (ex: clic sur une
 * aire dans "Avancement par aire"), jamais aux rafraichissements silencieux
 * des postes (toutes les 30s) - sinon le zoom/pan de l'utilisateur serait
 * reinitialise en permanence. `zoomKey` est une chaine arbitraire fournie
 * par l'appelant : la faire varier (ex. code d'aire, ou "tous" pour
 * revenir a la vue complete) declenche un nouveau cadrage sur les `postes`
 * courants (deja filtres par l'appelant si besoin).
 */
function AjusterVue({ postes, aeroport, zoomKey }: { postes: PosteCarte[]; aeroport: 'ORY' | 'CDG'; zoomKey: string }) {
  const map = useMap();
  const dernierDeclencheur = useRef<string | null>(null);

  useEffect(() => {
    const declencheur = `${aeroport}:${zoomKey}`;
    if (dernierDeclencheur.current === declencheur) {
      return;
    }
    dernierDeclencheur.current = declencheur;

    if (postes.length) {
      map.fitBounds(postes.map((p) => [p.latitude, p.longitude]), { padding: [30, 30] });
    } else {
      map.setView(AEROPORTS[aeroport].centre, AEROPORTS[aeroport].zoom);
    }
  }, [map, aeroport, zoomKey, postes]);

  return null;
}

export interface ParkingMapProps {
  postes: PosteCarte[];
  aeroport: 'ORY' | 'CDG';
  onSelect?: (poste: PosteCarte) => void;
  selectedCode?: string | null;
  hauteur?: number | string;
  /** Change cette valeur pour forcer un nouveau cadrage sur `postes` (ex: code d'aire cliquee, "tous" par defaut). */
  zoomKey?: string;
}

export function ParkingMap({ postes, aeroport, onSelect, selectedCode, hauteur = 600, zoomKey = 'tous' }: ParkingMapProps) {
  return (
    <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: hauteur }}>
      <MapContainer center={AEROPORTS[aeroport].centre} zoom={AEROPORTS[aeroport].zoom} maxZoom={20} style={{ height: '100%' }}>
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Imagerie &copy; Esri"
              maxZoom={20}
              maxNativeZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Plan">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; contributeurs OpenStreetMap"
              maxZoom={20}
              maxNativeZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <AjusterVue postes={postes} aeroport={aeroport} zoomKey={zoomKey} />

        {postes.map((poste) => (
          <CircleMarker
            key={poste.id}
            center={[poste.latitude, poste.longitude]}
            radius={poste.code === selectedCode ? 9 : 7}
            pathOptions={{
              color: poste.code === selectedCode ? '#1E3A5F' : '#ffffff',
              weight: poste.code === selectedCode ? 3 : 2,
              fillColor: COULEURS_STATUT_CARTE[poste.statutCarte],
              fillOpacity: 1,
              // RG-C05 : repere a contour pointille pour un poste sans situation.
              dashArray: poste.sansSituation ? '3, 3' : undefined,
            }}
            eventHandlers={{ click: () => onSelect?.(poste) }}
          >
            {/* Label permanent (pas seulement au survol) : demande
                utilisateur pour mieux identifier les postes d'un coup
                d'oeil, sans devoir cliquer ou survoler chacun. */}
            <Tooltip permanent direction="top" offset={[0, -8]} opacity={1} className="poste-label-tooltip">
              {poste.code}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

export default ParkingMap;
